import { useState } from "react";
import emailjs from "@emailjs/browser";
import Breadcrumb from "../breadcrumb/breadcrumb";
import SEO from "../SEO/SEO";
import {
  trackFormSubmit,
  trackPhoneClick,
  trackEmailClick,
} from "../../utils/analytics";
import "./contact.css";

export default function Contact() {
  const breadcrumbItems = [
    { label: "Accueil", url: "/" },
    { label: "Contact" },
  ];

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "reservation",
    message: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [formStatus, setFormStatus] = useState("");
  const [statusType, setStatusType] = useState(""); // "success" ou "error"
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Effacer l'erreur du champ modifié
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validation du format email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validation du format téléphone français
  const validatePhone = (phone) => {
    if (!phone) return true; // Champ optionnel
    // Accepte : 06 12 34 56 78, 0612345678, +33612345678, +33 6 12 34 56 78
    const phoneRegex = /^(\+33|0)[1-9](\s?\d{2}){4}$/;
    return phoneRegex.test(phone.replace(/\s+/g, " ").trim());
  };

  // Validation complète du formulaire
  const validateForm = () => {
    const errors = {};

    // Nom : minimum 2 caractères
    if (!formData.name.trim()) {
      errors.name = "Le nom est requis";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Le nom doit contenir au moins 2 caractères";
    }

    // Email : format valide
    if (!formData.email.trim()) {
      errors.email = "L'email est requis";
    } else if (!validateEmail(formData.email)) {
      errors.email = "L'email n'est pas valide";
    }

    // Téléphone : format français si renseigné
    if (formData.phone && !validatePhone(formData.phone)) {
      errors.phone = "Numéro de téléphone invalide (ex: 06 12 34 56 78)";
    }

    // Message : minimum 10 caractères
    if (!formData.message.trim()) {
      errors.message = "Le message est requis";
    } else if (formData.message.trim().length < 10) {
      errors.message = "Le message doit contenir au moins 10 caractères";
    } else if (formData.message.trim().length > 1000) {
      errors.message = "Le message ne doit pas dépasser 1000 caractères";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Valider le formulaire
    if (!validateForm()) {
      setFormStatus("❌ Veuillez corriger les erreurs dans le formulaire");
      setStatusType("error");
      setTimeout(() => {
        setFormStatus("");
        setStatusType("");
      }, 5000);
      return;
    }

    setIsSubmitting(true);
    setFormStatus("Envoi en cours...");
    setStatusType("");

    // Configuration EmailJS - Variables sécurisées depuis .env
    const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    // Préparation des données pour EmailJS
    const subjectText =
      formData.subject === "reservation"
        ? "Demande de réservation"
        : formData.subject === "info"
          ? "Demande d'information"
          : "Question générale";

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone || "Non renseigné",
      subject: subjectText,
      message: formData.message,
      to_name: "Grand Hôtel",
    };

    // Envoi via EmailJS
    emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY).then(
      () => {
        setIsSubmitting(false);
        setFormStatus(
          "✅ Message envoyé avec succès ! Nous vous répondrons rapidement.",
        );
        setStatusType("success");

        // Track l'événement dans Google Analytics
        trackFormSubmit(subjectText);

        // Réinitialiser le formulaire
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "reservation",
          message: "",
        });
        setFormErrors({});
        // Effacer le message après 5 secondes
        setTimeout(() => {
          setFormStatus("");
          setStatusType("");
        }, 5000);
      },
      (error) => {
        console.error("Erreur EmailJS:", error);
        setIsSubmitting(false);
        setFormStatus(
          "❌ Erreur lors de l'envoi. Veuillez réessayer ou nous contacter par téléphone.",
        );
        setStatusType("error");
        setTimeout(() => {
          setFormStatus("");
          setStatusType("");
        }, 5000);
      },
    );
  };

  return (
    <main className="contact-section">
      <SEO
        title="Contact - Location Grand Hôtel Châtel-Guyon | Réservations ☎ 06 95 39 85 19"
        description="Contactez-nous pour réserver votre appartement ou studio à Châtel-Guyon. Cure thermale, séjour touristique. Réponse rapide. ☎ 06 95 39 85 19 📧 marysabariguian@gmail.com"
        canonical="https://chatel-guyon-hotel-cure-thermes.fr/contact"
      />
      <Breadcrumb items={breadcrumbItems} />
      <div className="contact-container">
        <h1>Contactez-nous</h1>
        <p className="contact-intro">
          Vous avez une question ou souhaitez réserver un logement ? N'hésitez
          pas à nous contacter, nous vous répondrons dans les plus brefs délais.
        </p>

        <div className="contact-content">
          {/* Informations de contact */}
          <div className="contact-info">
            <h2>Nos coordonnées</h2>

            <div className="info-item">
              <div className="info-icon">📞</div>
              <div>
                <h3>Téléphone</h3>
                <a
                  href="tel:+33695398519"
                  onClick={() => trackPhoneClick("contact_page")}
                >
                  06 95 39 85 19
                </a>
                <p className="info-detail">Du lundi au dimanche, 9h-21h</p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">✉️</div>
              <div>
                <h3>Email</h3>
                <a
                  href="mailto:marysabariguian@gmail.com"
                  onClick={() => trackEmailClick("contact_page")}
                >
                  marysabariguian@gmail.com
                </a>
                <p className="info-detail">Réponse sous 24h</p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">📍</div>
              <div>
                <h3>Adresse</h3>
                <p>3 Place Brosson</p>
                <p>Châtel-Guyon, 63140, France</p>
                <p className="info-detail">À proximité des Thermes</p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">🌐</div>
              <div>
                <h3>Disponible aussi sur</h3>
                <a
                  href="https://www.airbnb.fr/users/profile/1469983327775128367"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Voir notre profil Airbnb (s'ouvre dans un nouvel onglet)"
                >
                  Airbnb
                </a>
                <p className="info-detail">Retrouvez nos annonces en ligne</p>
              </div>
            </div>
          </div>

          {/* Formulaire de contact */}
          <div className="contact-form-wrapper">
            <h2>Envoyez-nous un message</h2>
            <form
              onSubmit={handleSubmit}
              className="contact-form"
              aria-label="Formulaire de contact"
            >
              <div className="form-group">
                <label htmlFor="name">Nom complet *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Votre nom et prénom"
                  className={formErrors.name ? "input-error" : ""}
                  aria-invalid={formErrors.name ? "true" : "false"}
                  aria-describedby={formErrors.name ? "name-error" : undefined}
                />
                {formErrors.name && (
                  <p className="error-message" id="name-error" role="alert">
                    {formErrors.name}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="votre.email@exemple.com"
                  className={formErrors.email ? "input-error" : ""}
                  aria-invalid={formErrors.email ? "true" : "false"}
                  aria-describedby={
                    formErrors.email ? "email-error" : undefined
                  }
                />
                {formErrors.email && (
                  <p className="error-message" id="email-error" role="alert">
                    {formErrors.email}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Téléphone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="06 12 34 56 78"
                  className={formErrors.phone ? "input-error" : ""}
                  aria-invalid={formErrors.phone ? "true" : "false"}
                  aria-describedby={
                    formErrors.phone ? "phone-error" : undefined
                  }
                />
                {formErrors.phone && (
                  <p className="error-message" id="phone-error" role="alert">
                    {formErrors.phone}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="subject">Sujet *</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                >
                  <option value="reservation">Demande de réservation</option>
                  <option value="info">Demande d'information</option>
                  <option value="question">Question générale</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  placeholder="Décrivez-nous votre demande (dates souhaitées, nombre de personnes, questions...)"
                  className={formErrors.message ? "input-error" : ""}
                  aria-invalid={formErrors.message ? "true" : "false"}
                  aria-describedby={
                    formErrors.message ? "message-error" : undefined
                  }
                ></textarea>
                {formErrors.message && (
                  <p className="error-message" id="message-error" role="alert">
                    {formErrors.message}
                  </p>
                )}
                <p className="character-count">
                  {formData.message.length} / 1000 caractères
                </p>
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Envoi en cours..." : "Envoyer le message"}
              </button>

              {formStatus && (
                <p
                  className={`form-status ${statusType}`}
                  role="status"
                  aria-live="polite"
                >
                  {formStatus}
                </p>
              )}
            </form>
          </div>
        </div>

        {/* Informations pratiques */}
        <div className="contact-additional">
          <h2>Informations pratiques</h2>
          <div className="additional-grid">
            <div className="additional-item">
              <h3>🚗 Accès</h3>
              <p>Gare la plus proche : Riom-Châtel-Guyon (5 km)</p>
              <p>Navettes disponibles en saison de cure</p>
              <p>Parking public à proximité</p>
            </div>
            <div className="additional-item">
              <h3>🕐 Horaires</h3>
              <p>Du lundi au dimanche, 9h-21h</p>
              <p>Arrivée tardive possible (nous consulter)</p>
            </div>
            <div className="additional-item">
              <h3>💳 Paiement</h3>
              <p>Acompte à la réservation</p>
              <p>Solde à l'arrivée</p>
              <p>Espèces, chèque ou virement</p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
